import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createPainting } from '../../store/actions/paintingsActions';
import usePageTitle from '../../hooks/usePageTitle';

const AdminAddPainting = () => {
    const [name, setName] = useState('');
    const [price, setPrice] = useState('');
    const [description, setDescription] = useState('');
    const [images, setImages] = useState([]);
    const [error, setError] = useState('');

    const dispatch = useDispatch();
    const navigate = useNavigate();

    usePageTitle("Add Painting | Dinara Fariz");

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        setImages((prev) => [...prev, ...newFiles]);
    };

    const handleRemoveImage = (indexToRemove) => {
        setImages((prev) => prev.filter((_, index) => index !== indexToRemove));
    };

    const handlePriceChange = (e) => {
        const numericValue = e.target.value.replace(/\D/g, '');
        const formatted = new Intl.NumberFormat('ru-RU').format(numericValue);
        setPrice(formatted);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!name || !description || images.length === 0) {
            setError('Please fill in title, description and add at least one image.');
            return;
        }

        const formData = new FormData();
        formData.append('name', name);
        formData.append('price', price ? Number(price.replace(/\s/g, '')) : '');
        formData.append('description', description);
        images.forEach((image) => formData.append('images', image));

        try {
            await dispatch(createPainting(formData)).unwrap();

            setName('');
            setPrice('');
            setDescription('');
            setImages([]);

            navigate('/admin');
        } catch (err) {
            setError(typeof err === 'string' ? err : 'Failed to add painting.');
            console.error(err);
        }
    };

    return (
        <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-light uppercase tracking-[0.2em] text-gray-800 mb-8">
                Add Painting
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                    <p className="text-sm text-red-600">
                        {error}
                    </p>
                )}

                <div>
                    <label
                        htmlFor="name"
                        className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2"
                    >
                        Title
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition"
                        required
                    />
                </div>

                <div>
                    <label
                        htmlFor="price"
                        className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2"
                    >
                        Price (KZT)
                    </label>
                    <input
                        id="price"
                        type="text"
                        value={price}
                        onChange={handlePriceChange}
                        className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition"
                        placeholder="Optional"
                    />
                </div>

                <div>
                    <label
                        htmlFor="description"
                        className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2"
                    >
                        Description
                    </label>
                    <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full border border-gray-300 px-4 py-3 text-sm min-h-[140px] resize-y focus:outline-none focus:border-black transition"
                        required
                    />
                </div>

                <div>
                    <label
                        htmlFor="images"
                        className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2"
                    >
                        Images
                    </label>

                    <label
                        htmlFor="images"
                        className="block w-full border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 cursor-pointer hover:border-black hover:text-black transition"
                    >
                        Select one or more images
                    </label>

                    <input
                        id="images"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        required={images.length === 0}
                    />
                </div>

                {images.length > 0 && (
                    <div className="pt-2">
                        <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
                            Selected Images
                        </p>

                        <div className="flex flex-wrap gap-3">
                            {images.map((img, index) => (
                                <div key={index} className="relative">
                                    <img
                                        src={URL.createObjectURL(img)}
                                        alt={`preview ${index + 1}`}
                                        className="w-24 h-24 object-cover border border-gray-300"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveImage(index)}
                                        className="absolute -top-2 -right-2 w-6 h-6 border border-gray-300 bg-white text-sm hover:bg-gray-100 transition"
                                        aria-label={`Remove image ${index + 1}`}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="pt-2">
                    <button
                        type="submit"
                        className="border border-black px-6 py-3 text-sm uppercase tracking-[0.2em] text-black hover:bg-black hover:text-white transition"
                    >
                        Add Painting
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminAddPainting;